'use client'

import { useEffect } from 'react'

import type { AmplitudeConfig } from './schema'

declare global {
    interface Window {
        snowplow?: (...args: unknown[]) => void
    }
}

/** Paths excluded from Amplitude autocapture page views (matches ui-react) */
const EXCLUDED_PAGE_VIEW_PATTERNS = ['/depart', '/return', '/checkout']

/**
 * Initializes the Amplitude SDK with autocapture toggles driven by config.
 *
 * Both nexus and ui-react share the same API key, so the SDK natively shares
 * the device ID cookie — no custom bridging code needed.
 *
 * Autocapture categories (`elementInteractions`, `fileDownloads`,
 * `formInteractions`) are off in prod because Amplitude filters those
 * categories server-side. `pageViews` + `sessions` stay on everywhere.
 *
 * Flow:
 * 1. Add four enrichment plugins so they enrich all events including the
 *    initial page view: source, snowplow-ids, device-form-factor, page-type.
 *    The plugins set disjoint property keys; attach order does not affect
 *    the emitted payload today.
 * 2. Call amplitude.init() with shared key
 * 3. After init resolves: bridge Amplitude device ID to Snowplow global context
 *
 * Dynamic imports `@amplitude/analytics-browser` in useEffect to keep ~36KB off critical path.
 * Renders nothing (returns null).
 */
export function AmplitudeScript({
    config,
    appVersion,
}: {
    config: AmplitudeConfig
    appVersion: string
}) {
    useEffect(() => {
        async function initAmplitude() {
            const [
                amplitude,
                { createSnowplowIdsPlugin },
                { createSourcePlugin },
                { createDeviceFormFactorPlugin },
                { createPageTypePlugin },
            ] = await Promise.all([
                import('@amplitude/analytics-browser'),
                import('./snowplow-ids-plugin'),
                import('./source-plugin'),
                import('./device-form-factor-plugin'),
                import('./page-type-plugin'),
            ])

            amplitude.add(createSourcePlugin('nexus'))
            amplitude.add(createSnowplowIdsPlugin())
            amplitude.add(createDeviceFormFactorPlugin())
            amplitude.add(createPageTypePlugin())

            void amplitude
                .init(config.apiKey, {
                    serverUrl: config.serverUrl,
                    appVersion,
                    autocapture: {
                        attribution: config.autocapture.attribution
                            ? { resetSessionOnNewCampaign: true }
                            : false,
                        elementInteractions: config.autocapture.elementInteractions,
                        fileDownloads: config.autocapture.fileDownloads,
                        formInteractions: config.autocapture.formInteractions,
                        pageViews: config.autocapture.pageViews
                            ? {
                                  trackOn: () => {
                                      const path = window.location.pathname
                                      return !EXCLUDED_PAGE_VIEW_PATTERNS.some((pattern) =>
                                          path.includes(pattern),
                                      )
                                  },
                              }
                            : false,
                        sessions: config.autocapture.sessions,
                    },
                })
                .promise.then(() => {
                    // Bridge Amplitude device ID into Snowplow global context
                    const ampDeviceId = amplitude.getDeviceId()
                    if (ampDeviceId && window.snowplow) {
                        window.snowplow('addGlobalContexts', [
                            {
                                schema: 'iglu:dev.amp.snowplow/amp_id/jsonschema/1-0-0',
                                data: { ampClientId: ampDeviceId },
                            },
                        ])
                    }
                })
        }

        void initAmplitude()
    }, [config.apiKey, config.serverUrl, config.autocapture, appVersion])

    return null
}
